"use client";

import Image from "next/image";
import Link from "next/link";
import { motion, useReducedMotion } from "motion/react";
import { useEffect, useState } from "react";

import { SiteFooter } from "@/components/SiteFooter";
import { SiteHeader } from "@/components/SiteHeader";

import styles from "./page.module.css";

type PlanCurrency = "USD" | "CDF";
type PlanSymbol = "$" | "FC";
type PlanName = "Starter" | "Pro" | "Business" | "Premium";

type PlanFeature = {
  label: string;
  enabled: boolean;
};

type PricingPlan = {
  name: PlanName;
  duration: number;
  price: number;
  currency: PlanCurrency;
  symbol: PlanSymbol;
  lifetime?: boolean;
  features: PlanFeature[];
};

const FALLBACK_PLAN_CAPABILITIES = [
  "Synchronisation sur tous les appareils",
  "Ventes visibles en temps réel",
  "Stock visible en temps réel",
  "Impression de facture",
  "Création d'utilisateurs et gestion des rôles",
  "Définition des règles de la pharmacie",
  "Téléchargement du rapport en JSON ou PDF",
  "Travail possible même sans Internet",
  "Statistiques détaillées",
  "Utilisation sur mobile, POS et navigateur",
  "Multi-pharmacie",
] as const;

const createFallbackFeatures = (planName: PlanName): PlanFeature[] =>
  FALLBACK_PLAN_CAPABILITIES.map((label) => ({
    label,
    enabled: label === "Multi-pharmacie" ? planName === "Premium" : true,
  }));

const FALLBACK_PRICING_PLANS: PricingPlan[] = [
  {
    name: "Starter",
    duration: 10,
    price: 0,
    currency: "USD",
    symbol: "$",
    features: createFallbackFeatures("Starter"),
  },
  {
    name: "Pro",
    duration: 30,
    price: 5,
    currency: "USD",
    symbol: "$",
    features: createFallbackFeatures("Pro"),
  },
  {
    name: "Business",
    duration: 365,
    price: 50,
    currency: "USD",
    symbol: "$",
    features: createFallbackFeatures("Business"),
  },
  {
    name: "Premium",
    duration: 0,
    price: 499,
    currency: "USD",
    symbol: "$",
    lifetime: true,
    features: createFallbackFeatures("Premium"),
  },
];

const WITHOUT_PHARMAPP_POINTS = [
  "Pertes invisibles",
  "Vendeurs non contrôlés",
  "Calculs fatiguants le soir",
  "Erreurs dans le stock",
] as const;

const WITH_PHARMAPP_POINTS = [
  "Chaque vente est enregistrée",
  "Le stock se met à jour automatiquement",
  "La caisse est calculée en temps réel",
  "Vous pouvez vérifier à distance",
] as const;

const TRANSFORMATION_BENEFITS = [
  "Moins de pertes",
  "Moins de stress",
  "Plus de contrôle",
  "Plus de temps libre",
] as const;

const PLAN_ORDER: PlanName[] = ["Starter", "Pro", "Business", "Premium"];

const normalizePlanName = (value: unknown): PlanName | null => {
  if (typeof value !== "string") return null;

  const normalized = value.trim().toLowerCase();
  if (normalized === "starter") return "Starter";
  if (normalized === "pro") return "Pro";
  if (normalized === "business") return "Business";
  if (normalized === "premium") return "Premium";
  return null;
};

const toNumber = (value: unknown, fallback: number) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const normalizeFeatures = (value: unknown, fallback: PlanFeature[]): PlanFeature[] => {
  if (!Array.isArray(value)) return fallback;

  const normalized = value
    .map((item) => {
      if (!item || typeof item !== "object") return null;

      const rawFeature = item as Record<string, unknown>;
      const labelSource = rawFeature.label ?? rawFeature.name ?? rawFeature.title ?? rawFeature.text;
      if (typeof labelSource !== "string" || labelSource.trim().length === 0) return null;

      const enabled = Boolean(
        rawFeature.enabled ?? rawFeature.available ?? rawFeature.included ?? rawFeature.value,
      );

      return {
        label: labelSource.trim(),
        enabled,
      };
    })
    .filter((feature): feature is PlanFeature => Boolean(feature));

  return normalized.length > 0 ? normalized : fallback;
};

const normalizePlan = (value: unknown): PricingPlan | null => {
  if (!value || typeof value !== "object") return null;

  const rawPlan = value as Record<string, unknown>;
  const name = normalizePlanName(rawPlan.name);
  if (!name) return null;

  const fallbackPlan = FALLBACK_PRICING_PLANS.find((plan) => plan.name === name);
  if (!fallbackPlan) return null;

  const currency = rawPlan.currency === "CDF" ? "CDF" : fallbackPlan.currency;
  const symbol = rawPlan.symbol === "FC" || currency === "CDF" ? "FC" : "$";
  const duration = toNumber(rawPlan.duration ?? rawPlan.days ?? rawPlan.durationInDays, fallbackPlan.duration);
  const price = toNumber(rawPlan.price ?? rawPlan.amount, fallbackPlan.price);
  const lifetime = Boolean(rawPlan.lifetime ?? rawPlan.unlimited ?? rawPlan.isLifetime) || name === "Premium";
  const features = normalizeFeatures(rawPlan.features ?? rawPlan.description, fallbackPlan.features);

  return {
    name,
    duration: lifetime ? 0 : duration,
    price,
    currency,
    symbol,
    lifetime,
    features,
  };
};

const extractPlans = (payload: unknown): PricingPlan[] => {
  const source = Array.isArray(payload)
    ? payload
    : Array.isArray((payload as { plans?: unknown[] })?.plans)
      ? (payload as { plans: unknown[] }).plans
      : Array.isArray((payload as { data?: unknown[] })?.data)
        ? (payload as { data: unknown[] }).data
        : [];

  return source
    .map(normalizePlan)
    .filter((plan): plan is PricingPlan => Boolean(plan))
    .sort((left, right) => PLAN_ORDER.indexOf(left.name) - PLAN_ORDER.indexOf(right.name));
};

export default function Home() {
  const shouldReduceMotion = useReducedMotion();
  const [pricingPlans, setPricingPlans] = useState<PricingPlan[]>(FALLBACK_PRICING_PLANS);

  const features = [
    {
      icon: "bi bi-broadcast-pin",
      badge: "Temps réel",
      title: "Ventes suivies en temps réel",
      text: "Les opérations de vente sont enregistrées avec une lecture claire des mouvements, des quantités et des montants utiles au pilotage quotidien.",
    },
    {
      icon: "bi bi-upc-scan",
      badge: "Recherche rapide",
      title: "Produits retrouvés plus rapidement",
      text: "La recherche peut se faire par saisie ou par scan, avec l'affichage de l'emplacement du produit pour accélérer le service au comptoir.",
    },
    {
      icon: "bi bi-box-seam",
      badge: "Stock réel",
      title: "Stock restant visible immédiatement",
      text: "Le stock disponible est calculé à partir des entrées et des ventes déjà enregistrées, pour afficher ce qu'il reste réellement sans approximation.",
    },
    {
      icon: "bi bi-cash-stack",
      badge: "Caisse",
      title: "Caisse suivie sans confusion",
      text: "Le montant attendu en caisse est détaillé avec les ventes, les dépôts et les retraits du jour, par vendeur ou en vue globale.",
    },
    {
      icon: "bi bi-file-earmark-arrow-down",
      badge: "Rapports",
      title: "Rapports générés sans travail manuel",
      text: "Les rapports peuvent être exportés directement en JSON ou en PDF, sans recompilation manuelle des chiffres ni ressaisie de fin de journée.",
    },
    {
      icon: "bi bi-wifi-off",
      badge: "Continuité",
      title: "Activité maintenue malgré une connexion faible",
      text: "Grâce à la base locale et à la réplication, l'équipe peut continuer à travailler puis resynchroniser les données dès que la connexion revient.",
    },
    {
      icon: "bi bi-person-badge",
      badge: "Contrôle vendeur",
      title: "Vendeurs autonomes, contrôle préservé",
      text: "Les rôles et les réglages permettent d'encadrer précisément les actions des vendeurs, sans ralentir leur travail quotidien.",
    },
    {
      icon: "bi bi-diagram-3",
      badge: "Multi-succursales",
      title: "Plusieurs pharmacies gérées dans une seule interface",
      text: "La gestion de plusieurs succursales peut être centralisée dans un environnement simple et lisible, pour garder une vision cohérente de l'activité.",
    },
    {
      icon: "bi bi-stars",
      badge: "Professionnalisation",
      title: "Gestion plus professionnelle, sans lourdeur",
      text: "Le portail s'appuie sur une logique claire de vente, de stock, de caisse, d'utilisateurs et de rapports pour structurer la pharmacie sans complexité inutile.",
    },
    {
      icon: "bi bi-lightning-charge",
      badge: "Automatisation",
      title: "Fin des cahiers surchargés et des calculs interminables",
      text: "Les calculs du quotidien, le suivi de stock et les vérifications de caisse sont automatisés pour réduire les erreurs et libérer du temps utile à l'équipe.",
    },
  ];

  const onboardingSteps = [
    {
      number: "01",
      badge: "Créer votre compte",
      title: "Ouvrez votre espace entreprise en quelques minutes",
      text: "Cliquez sur Commencer maintenant, puis renseignez le nom de votre entreprise, votre prénom, votre nom, votre adresse e-mail si disponible et votre numéro de téléphone.",
    },
    {
      number: "02",
      badge: "Télécharger l'application",
      title: "Installez Pharmapp sur votre appareil Android",
      text: "Une fois votre compte créé, accédez à l'option de téléchargement, puis installez l'application sur votre POS ou votre téléphone Android pour démarrer rapidement.",
    },
    {
      number: "03",
      badge: "Paramétrer",
      title: "Configurez votre première succursale et lancez l'essai",
      text: "Ajoutez votre première succursale, ajustez les réglages essentiels de votre pharmacie et commencez immédiatement votre période d'essai gratuite.",
    },
  ];

  const fadeUp = {
    initial: { opacity: 0, y: shouldReduceMotion ? 0 : 20 },
    animate: { opacity: 1, y: 0 },
  };

  const formatDuration = (plan: PricingPlan) => {
    if (plan.lifetime) return "À vie";
    return `${plan.duration} jours`;
  };

  useEffect(() => {
    const apiBaseUrl = process.env.NEXT_PUBLIC_API_URL?.trim();
    if (!apiBaseUrl) return;
    const apiRoot = `${apiBaseUrl.replace(/\/+$/, "")}/pharmacy`;

    const controller = new AbortController();

    const loadPlans = async () => {
      try {
        const response = await fetch(`${apiRoot}/getplan`, {
          method: "GET",
          signal: controller.signal,
          headers: {
            Accept: "application/json",
          },
        });

        if (!response.ok) return;

        const payload = (await response.json()) as unknown;
        const remotePlans = extractPlans(payload);

        if (remotePlans.length > 0) {
          setPricingPlans(remotePlans);
        }
      } catch {
        setPricingPlans(FALLBACK_PRICING_PLANS);
      }
    };

    void loadPlans();

    return () => controller.abort();
  }, []);

  return (
    <>
      <SiteHeader />
      <div className={styles.page}>
        <main className={styles.main}>
        <section className={styles.hero} id="accueil">
          <motion.div
            className={styles.heroCopy}
            initial={fadeUp.initial}
            animate={fadeUp.animate}
            transition={{ duration: 0.48, delay: 0.08, ease: "easeOut" }}
          >
            <span className={styles.kicker}>Contrôlez votre pharmacie même à distance</span>
            <h1>Ne perdez plus d’argent sans le savoir. Contrôlez vos ventes, votre stock et votre caisse, même sans être sur place.</h1>
            <p>
              Pharmapp enregistre chaque vente, suit votre stock et calcule automatiquement votre caisse, pour que vous sachiez exactement ce qui se passe, à tout moment.
            </p>

            <div className={styles.ctas}>
              <Link href="/purchase" className={`${styles.cta} ${styles.ctaPrimary}`}>
                Commencer l’essai gratuit dès maintenant
              </Link>
            </div>
          </motion.div>

          <motion.aside
            className={styles.heroPanel}
            initial={{ opacity: 0, y: shouldReduceMotion ? 0 : 24, scale: shouldReduceMotion ? 1 : 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.52, delay: 0.16, ease: "easeOut" }}
          >
            <motion.div
              className={styles.previewShell}
              animate={
                shouldReduceMotion
                  ? undefined
                  : {
                      y: [0, -6, 0],
                    }
              }
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            >
              <Image
                src="/homescreen_screenshot_2.PNG"
                alt="Capture de l'ecran d'accueil Pharmapp sur terminal mobile"
                width={512}
                height={1024}
                priority
                className={styles.previewImage}
              />
            </motion.div>

            <motion.p
              className={styles.previewCaption}
              initial={{ opacity: 0, y: shouldReduceMotion ? 0 : 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.28, ease: "easeOut" }}
            >
              Une interface claire, intuitive et pensée pour professionnaliser la gestion quotidienne de votre pharmacie.
            </motion.p>
          </motion.aside>
        </section>

        <section className={`${styles.section} ${styles.transformationSection}`} aria-labelledby="transformation-title">
          <motion.div
            className={styles.transformationShell}
            initial={{ opacity: 0, y: shouldReduceMotion ? 0 : 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.42, ease: "easeOut" }}
          >
            <div className={styles.transformationIntro}>
              <span className={styles.kicker}>Avant / Après</span>
              <h2 id="transformation-title">La différence se ressent dès le premier soir.</h2>
              <p className={styles.transformationLead}>
                Sans visibilité, les pertes se cachent dans les détails et la fermeture de journée devient
                plus lourde qu’elle ne devrait l’être. Avec Pharmapp, chaque mouvement devient clair,
                vérifiable et beaucoup plus simple à piloter.
              </p>
            </div>

            <div className={styles.comparisonGrid}>
              <motion.article
                className={`${styles.comparisonCard} ${styles.comparisonCardWarning}`}
                initial={{ opacity: 0, y: shouldReduceMotion ? 0 : 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.38, delay: 0.04, ease: "easeOut" }}
              >
                <div className={styles.comparisonCardTop}>
                  <span className={`${styles.comparisonTag} ${styles.comparisonTagWarning}`}>Sans Pharmapp</span>
                  <h3>Les problèmes s’accumulent sans toujours se voir.</h3>
                  <p className={styles.comparisonLabel}>
                    Vous travaillez, vous vérifiez, vous recalculez, mais vous gardez quand même un doute.
                  </p>
                </div>

                <ul className={styles.comparisonList}>
                  {WITHOUT_PHARMAPP_POINTS.map((item) => (
                    <li key={item}>
                      <span className={`${styles.comparisonItemIcon} ${styles.comparisonItemIconWarning}`}>
                        <i className="bi bi-dash-lg" aria-hidden="true"></i>
                      </span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.article>

              <motion.article
                className={`${styles.comparisonCard} ${styles.comparisonCardSuccess}`}
                initial={{ opacity: 0, y: shouldReduceMotion ? 0 : 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.38, delay: 0.12, ease: "easeOut" }}
              >
                <div className={styles.comparisonCardTop}>
                  <span className={`${styles.comparisonTag} ${styles.comparisonTagSuccess}`}>Avec Pharmapp</span>
                  <h3>Vous reprenez la main sur ce qui compte vraiment.</h3>
                  <p className={styles.comparisonLabel}>
                    Les ventes, le stock et la caisse avancent ensemble, avec une lecture simple même à distance.
                  </p>
                </div>

                <ul className={styles.comparisonList}>
                  {WITH_PHARMAPP_POINTS.map((item) => (
                    <li key={item}>
                      <span className={`${styles.comparisonItemIcon} ${styles.comparisonItemIconSuccess}`}>
                        <i className="bi bi-check-lg" aria-hidden="true"></i>
                      </span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.article>
            </div>

            <motion.div
              className={styles.transformationCta}
              initial={{ opacity: 0, y: shouldReduceMotion ? 0 : 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.35 }}
              transition={{ duration: 0.42, delay: 0.08, ease: "easeOut" }}
            >
              <div className={styles.transformationCtaCopy}>
                <p className={styles.transformationCtaText}>
                  Avec Pharmapp, moins de pertes, moins de stress, plus de contrôle, plus de temps libre.
                </p>
                <p className={styles.transformationCtaSubtext}>
                  Chaque journée sans bon système laisse de la place aux erreurs silencieuses. Commencez
                  maintenant et donnez à votre pharmacie une gestion plus sereine, plus nette et plus professionnelle.
                </p>
                <div className={styles.transformationBenefitRow}>
                  {TRANSFORMATION_BENEFITS.map((benefit) => (
                    <span key={benefit} className={styles.transformationBenefit}>
                      {benefit}
                    </span>
                  ))}
                </div>
              </div>

              <Link href="/purchase" className={`${styles.cta} ${styles.ctaPrimary}`}>
                Commencer maintenant
              </Link>
            </motion.div>
          </motion.div>
        </section>

        <section className={styles.section} id="fonctionnalites">
          <motion.div
            className={styles.sectionIntro}
            initial={{ opacity: 0, y: shouldReduceMotion ? 0 : 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.42, ease: "easeOut" }}
          >
            <span className={styles.kicker}>Fonctionnalités</span>
            <h2>Les fonctionnalités que vous trouverez dans Pharmapp</h2>
          </motion.div>

          <div className={styles.cardGrid}>
            {features.map((item, index) => (
              <motion.article
                key={item.title}
                className={styles.featureCard}
                initial={{ opacity: 0, y: shouldReduceMotion ? 0 : 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.35 }}
                transition={{ duration: 0.38, delay: index * 0.08, ease: "easeOut" }}
              >
                <div className={styles.featureBadge}>
                  <i className={item.icon}></i>
                  <span>{item.badge}</span>
                </div>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </motion.article>
            ))}
          </div>
        </section>

        <section className={styles.section} id="tarifs">
          <motion.div
            className={styles.sectionIntro}
            initial={{ opacity: 0, y: shouldReduceMotion ? 0 : 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.35 }}
            transition={{ duration: 0.42, ease: "easeOut" }}
          >
            <span className={styles.kicker}>Tarifs</span>
            <h2>Des offres claires et évolutives, pensées pour accompagner chaque pharmacie selon sa taille, son rythme et ses ambitions.</h2>
          </motion.div>

          <div className={styles.pricingGrid}>
            {pricingPlans.map((plan, index) => (
              <motion.article
                key={plan.name}
                className={styles.pricingCard}
                initial={{ opacity: 0, y: shouldReduceMotion ? 0 : 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.38, delay: index * 0.06, ease: "easeOut" }}
              >
                <div className={styles.pricingTop}>
                  <div className={styles.planBadge}>{plan.name}</div>
                  <h3 className={styles.planTitle}>{plan.name}</h3>
                  <div className={styles.planPrice}>
                    <span className={styles.planAmount}>{plan.price}</span>
                    <span className={styles.planSymbol}>{plan.symbol}</span>
                  </div>
                  <div className={styles.planMeta}>
                    <span>{formatDuration(plan)}</span>
                    
                  </div>
                </div>

                <div className={styles.pricingBody}>
                  {plan.features.map((feature) => {
                    const enabled = feature.enabled;

                    return (
                      <div key={`${plan.name}-${feature.label}`} className={styles.pricingItem}>
                        <span className={enabled ? styles.statusYes : styles.statusNo}>
                          <i className={`bi ${enabled ? "bi-check-circle-fill" : "bi-x-circle-fill"}`}></i>
                        </span>
                        <span>{feature.label}</span>
                      </div>
                    );
                  })}
                </div>
              </motion.article>
            ))}
          </div>
        </section>

        <section className={styles.section} id="etapes">
          <motion.div
            className={styles.sectionIntro}
            initial={{ opacity: 0, y: shouldReduceMotion ? 0 : 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.35 }}
            transition={{ duration: 0.42, ease: "easeOut" }}
          >
            <span className={styles.kicker}>Étapes à suivre</span>
            <h2>Rendez-vous la vie facile avec Pharmapp en trois étapes.</h2>
            <p className={styles.sectionLead}>
              Chaque jour passé sans un bon système coûte du temps, de l’argent et de la clarté. Commencez
              maintenant et donnez à votre pharmacie un cadre plus simple, plus rapide et plus professionnel.
            </p>
          </motion.div>

          <div className={styles.stepsGrid}>
            {onboardingSteps.map((step, index) => (
              <motion.article
                key={step.number}
                className={styles.stepCard}
                initial={{ opacity: 0, y: shouldReduceMotion ? 0 : 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.35 }}
                transition={{ duration: 0.38, delay: index * 0.08, ease: "easeOut" }}
              >
                <div className={styles.stepTop}>
                  <span className={styles.stepNumber}>{step.number}</span>
                  <span className={styles.stepBadge}>{step.badge}</span>
                </div>
                <h3>{step.title}</h3>
                <p>{step.text}</p>
              </motion.article>
            ))}
          </div>

          <motion.div
            className={styles.stepsCtaWrap}
            initial={{ opacity: 0, y: shouldReduceMotion ? 0 : 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.35 }}
            transition={{ duration: 0.42, ease: "easeOut" }}
          >
            <p className={styles.stepsCtaText}>Prenez de l’avance dès aujourd’hui pendant que d’autres gèrent encore dans l’urgence.</p>
            <Link href="/purchase" className={`${styles.cta} ${styles.ctaPrimary}`}>
              Commencer maintenant
            </Link>
          </motion.div>
        </section>
        </main>
      </div>
      <SiteFooter />
    </>
  );
}
